import { EditOutlined, LoadingOutlined } from "@ant-design/icons";
import { Boundary, ImageLoader } from "components/common";
import {
  ALL_IN_ONE_PLAN,
  PAYMENT_PROVIDERS,
  SOCIAL_PLAN,
  createDefaultPaymentLinks,
  getPlanLabel,
  getUserPlan,
} from "constants/plans";
import { Formik } from "formik";
import {
  useDocumentTitle,
  useFileHandler,
  useModal,
  useScrollTop,
  usePDFHandler,
} from "hooks";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "redux/actions/miscActions";
import { updateProfile } from "redux/actions/profileActions";
import * as Yup from "yup";
import { isSocialMediaLink, isValidWhatsAppInput } from "utils/profileLinks";
import ConfirmModal from "./ConfirmModal";
import ConfirmModalpass from "./ConfirmModalpass";

import EditForm from "./EditForm";

const FormSchema = Yup.object().shape({
  fullname: Yup.string()
    .min(4, "Full name should be at least 4 characters.")
    .max(60, "Full name should be only be 4 characters long.")
    .required("Full name is required"),
  newPassword: Yup.string().min(8, "Password should be at least 8 characters."),

  email: Yup.string()
    .email("Email is not valid.")
    .required("Email is required."),
  address: Yup.string(),
  gMaps: Yup.string(),

  // mobile: Yup.object()
  //   .shape({
  //     country: Yup.string(),
  //     countryCode: Yup.string(),
  //     dialCode: Yup.string(),
  //     value: Yup.string().min(12, 'Mobile number should be at least 11 number.')
  //     .max(12, 'Mobile should be only be 11 numbers long.')
  //   }),
  //   bisMobile: Yup.object()
  //   .shape({
  //     country: Yup.string(),
  //     countryCode: Yup.string(),
  //     dialCode: Yup.string(),
  //     value: Yup.string().min(12, 'Mobile number should be at least 11 number.')
  //     .max(12, 'Mobile should be only be 11 numbers long.')
  //      }),

  mobile: Yup.string(),

  bisMobile: Yup.string(),

  whatsapp: Yup.string().test(
    "whatsapp-number-or-link",
    "Enter a WhatsApp number with country code or a valid wa.me/WhatsApp link.",
    isValidWhatsAppInput
  ),
  whatsappLabel: Yup.string().max(60, "WhatsApp link text must be 60 characters or less."),
  friends: Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      label: Yup.string().max(60, "Link text must be 60 characters or less."),
      icon: Yup.string(),
    })
  ),
  paymentLinks: Yup.array().of(
    Yup.object().shape({
      provider: Yup.string().oneOf(PAYMENT_PROVIDERS.map((provider) => provider.value)),
      url: Yup.string(),
      label: Yup.string().max(80, "Payment text must be 80 characters or less."),
    })
  ),
});

const getInitialPaymentLinks = (savedLinks = []) => {
  const defaults = createDefaultPaymentLinks();
  const usedIndexes = [];
  const requiredLinks = defaults.map((defaultLink) => {
    const savedIndex = savedLinks.findIndex(
      (link, index) => link.provider === defaultLink.provider && !usedIndexes.includes(index)
    );

    if (savedIndex === -1) return defaultLink;
    usedIndexes.push(savedIndex);
    return savedLinks[savedIndex];
  });

  return [
    ...requiredLinks,
    ...savedLinks.filter((link, index) => (
      PAYMENT_PROVIDERS.some((provider) => provider.value === link.provider)
      && !usedIndexes.includes(index)
    )),
  ].map((link) => ({ ...link, label: link.label || "" }));
};

const EditProfile = () => {
  useDocumentTitle("Edit Account | TaKKa");
  useScrollTop();

  const modal = useModal();
  const dispatch = useDispatch();

  useEffect(
    () => () => {
      dispatch(setLoading(false));
    },
    []
  );

  const { profile, auth, isLoading } = useSelector((state) => ({
    profile: state.profile,
    auth: state.auth,
    isLoading: state.app.loading,
  }));
  const plan = getUserPlan(profile.plan);

  const initFormikValues = {
    fullname: profile.fullname || "",

    email: profile.email || "",

    job: profile.job || "",
    website: profile.website || "",
    bio: profile.bio || "",
    newPassword: "",

    address: profile.address || "",
    gMaps: profile.gMaps || "",

    whatsapp: profile.whatsapp || "",
    whatsappLabel: profile.whatsappLabel || "",

    mobile: profile.mobile || "",
    bisMobile: profile.bisMobile || "",

    friends: profile.friends ? profile.friends.map((friend, index) => ({
      ...friend,
      id: friend.id || `legacy-link-${index}`,
      label: friend.label || "",
      icon: friend.icon || "",
    })) : [
      {
        id: "legacy-link-0",
        name: "",
        label: "",
        icon: "",
      },
    ],
    paymentLinks: getInitialPaymentLinks(profile.paymentLinks || []),
  };

  const { imageFile, isFileLoading, onFileChange } = useFileHandler({
    avatar: {},
    banner: {},
  });

  const { pdfFile, pdfFileName, isPDFLoading, onPDFChange } = usePDFHandler({
    pdf: {},
  });

  const update = (form, credentials = {}) => {
    const linkIconFiles = form.friends
      .filter(
        (friend) =>
          plan === ALL_IN_ONE_PLAN &&
          typeof File !== "undefined" && friend.iconFile instanceof File
      )
      .map((friend) => ({ id: friend.id, file: friend.iconFile }));
    const friends = form.friends.map(({ iconFile, iconPreview, ...friend }) => friend);

    //console.log (pdfFile)
    dispatch(
      updateProfile({
        updates: {
          fullname: form.fullname,
          plan,

          email: form.email,

          address: form.address,
          gMaps: form.gMaps,
          friends,
          paymentLinks: form.paymentLinks.map(({ provider, url, label }) => ({
            provider,
            url,
            label: label || "",
          })),
          job: form.job,
          bio: form.bio,

          website: form.website,
          whatsapp: form.whatsapp,
          whatsappLabel: form.whatsappLabel,

          mobile: form.mobile,
          bisMobile: form.bisMobile,

          pdf: profile.pdf ? profile.pdf : "",
          pdfName:
            typeof pdfFileName === "string"
              ? pdfFileName
              : profile.pdfName
              ? profile.pdfName
              : "",
          avatar: profile.avatar,
          banner: profile.banner,
        },
        files: {
          bannerFile: imageFile.banner.file,
          avatarFile: imageFile.avatar.file,
          pdffile: pdfFile.pdf.file,
          linkIconFiles,
        },
        credentials,
      })
    );
  };

  const onConfirmUpdate = (form, password) => {
    if (password) {
      update(form, { email: form.email, password });
    }
  };

  const onConfirmUpdatepass = (form, password) => {
    if (password) {
      update(form, {
        email: form.email,
        password: password,
        newPassword: form.newPassword,
      });
    }
  };

  const onSubmitUpdate = (form) => {
    if (plan === SOCIAL_PLAN) {
      const savedLinkNames = new Set(
        (profile.friends || []).map((friend) => String(friend.name || "").trim())
      );
      const unsupportedNewLink = form.friends.find((friend) => {
        const link = String(friend.name || "").trim();
        return link && !isSocialMediaLink(link) && !savedLinkNames.has(link);
      });

      if (unsupportedNewLink) {
        window.alert(
          "The Social Media plan accepts social-network links only. Use All in One for custom links and custom logos."
        );
        return;
      }
    }

    // check if data has changed
    const fieldsChanged = Object.keys(form).some(
      (key) => profile[key] !== form[key]
    );

    if (
      fieldsChanged ||
      Boolean(
        imageFile.banner.file ||
        imageFile.avatar.file ||
        plan === ALL_IN_ONE_PLAN && form.friends.some(
          (friend) =>
            typeof File !== "undefined" && friend.iconFile instanceof File
        )
      )
    ) {
      if (form.newPassword.length > 5) {
        modal.onOpenpassModal();
      }

      if (form.email !== profile.email) {
        modal.onOpenModal();
      } else {
        update(form);
      }
    }
  };

  return (
    <Boundary>
      <div className="edit-user">
        <h3 className="text-center">{getPlanLabel(plan)} Dashboard</h3>
        <Formik
          initialValues={initFormikValues}
          validateOnChange
          validationSchema={FormSchema}
          onSubmit={onSubmitUpdate}
        >
          {() => (
            <>
              <div className="user-profile-banner">
                <div className="user-profile-banner-wrapper">
                  <ImageLoader
                    alt="Banner"
                    className="user-profile-banner-img"
                    src={imageFile.banner.url || profile.banner}
                  />
                  {isFileLoading ? (
                    <div className="loading-wrapper">
                      <LoadingOutlined />
                    </div>
                  ) : (
                    <label
                      className="edit-button edit-banner-button"
                      htmlFor="edit-banner"
                    >
                      <input
                        accept="image/x-png,image/jpeg"
                        disabled={isLoading}
                        hidden
                        id="edit-banner"
                        onChange={(e) =>
                          onFileChange(e, { name: "banner", type: "single" })
                        }
                        type="file"
                      />
                      <EditOutlined />
                    </label>
                  )}
                </div>
                <div className="user-profile-avatar-wrapper">
                  <ImageLoader
                    alt="Avatar"
                    className="user-profile-img"
                    src={imageFile.avatar.url || profile.avatar}
                  />
                  {isFileLoading ? (
                    <div className="loading-wrapper">
                      <LoadingOutlined />
                    </div>
                  ) : (
                    <label
                      className="edit-button edit-avatar-button"
                      htmlFor="edit-avatar"
                    >
                      <input
                        accept="image/x-png,image/jpeg"
                        disabled={isLoading}
                        hidden
                        id="edit-avatar"
                        onChange={(e) =>
                          onFileChange(e, { name: "avatar", type: "single" })
                        }
                        type="file"
                      />
                      <EditOutlined />
                    </label>
                  )}
                  <br />
                  <br />
                  {plan === ALL_IN_ONE_PLAN ? <div style={{ width: 400 }}>
                    {profile.pdf && typeof profile.pdf === "string" ? (
                      <span>
                        <strong>PDF File: </strong>
                        <a href={profile.pdf}>
                          {typeof pdfFileName === "string"
                            ? pdfFileName
                            : profile.pdfName
                            ? profile.pdfName
                            : "Not Set"}
                        </a>{" "}
                        <br />
                        <br />
                      </span>
                    ) : null}

                    <label
                      htmlFor="edit-pdf"
                      style={{ width: 120 }}
                      className="button button-small"
                    >
                      {" "}
                      {profile.pdf ? "Change" : "Add"} PDF File
                      <input
                        accept="application/pdf"
                        disabled={isLoading}
                        label="PDF File"
                        hidden
                        id="edit-pdf"
                        onChange={(e) =>
                          onPDFChange(e, { name: "pdf", type: "single" })
                        }
                        type="file"
                      />
                    </label>
                  </div> : null}
                </div>
              </div>
              <br />
              <br />
              <br />

              <EditForm authProvider={auth.provider} isLoading={isLoading} plan={plan} />

              <ConfirmModal onConfirmUpdate={onConfirmUpdate} modal={modal} />

              <ConfirmModalpass
                onConfirmUpdate={onConfirmUpdatepass}
                modal={modal}
              />
            </>
          )}
        </Formik>
      </div>
    </Boundary>
  );
};

export default EditProfile;
